import { Dayjs } from "dayjs";
import Decimal from "decimal.js";
import { PMTiles } from "pmtiles";
import { gslForecastKey, gslTargetKey, usesGslAnalysis } from "./GslTime";
import { type MapMeta, type MapModel, getMapMeta } from "./MapMeta";
import { msgForecastKey, msgTargetKey, usesMsgAnalysis } from "./MsgTime";
import { PrefetchQueue } from "./PrefetchQueue";
import { applySnowPalette, snowStorageOf, snowStyleOf } from "./SnowPalette";
import { dataPath } from "./paths";

export type CanvasTileLevel = 0 | 1 | 2 | 3;
export type CanvasTileElements = [string, string, string];

interface TileRange {
    top: number;
    bottom: number;
    left: number;
    right: number;
}

interface TileJob {
    key: string;
    archiveUrl: string;
    element: string;
    level: CanvasTileLevel;
    row: number;
    column: number;
    dx: number;
    dy: number;
    dw: number;
    dh: number;
}

interface TileSource {
    data: ArrayBuffer | null;
    factor: number;
    offsetX: number;
    offsetY: number;
}

const SVG_NS = "http://www.w3.org/2000/svg";
const PMTILES_ELEMENTS = new Set([
    "MSMR", "MSMC", "MSMRG", "MSMSj", "MSMSh", "MSMSv", "MSMT", "MSMV",
    "LFMR", "LFMC", "LFMSj", "LFMSh", "LFMSv", "LFMT",
    "GSMR", "GSMRG", "GSMC", "GSMSj", "GSMSh", "GSMSv", "GSMV",
    "CWMH", "GS5R", "GS5Sj", "GS5Sh", "QMAR", "QMASj", "QMASh",
    "GSAR", "GSASj", "GSASh", "GSASv",
    "GSLR",
    "MSGL",
]); // biome-ignore format:

export class CanvasTiles {
    private readonly main = this.createLayer("gpv_tiles_main", false);
    private readonly cloud = this.createLayer("gpv_tiles_cloud", true);
    private readonly archives = new Map<string, Promise<PMTiles>>();
    private readonly archiveOrder: string[] = [];
    private readonly bitmaps = new Map<string, Promise<ImageBitmap | null>>();
    private readonly bitmapOrder: string[] = [];
    private readonly bytes = new Map<string, ArrayBuffer | null>();
    private readonly byteOrder: string[] = [];
    private byteSize = 0;
    private byteScope = "";
    private composeSequence = 0;
    private cacheScope = "";
    private active = false;
    private lastComposeKey = "";
    // 先読みは全てこのキュー経由 (同時3本)。表示用の取得はキューを通さない
    private readonly prefetchQueue = new PrefetchQueue(3);
    private progressCallback: () => void = () => {};

    constructor(private readonly dataBase = dataPath("").replace(/\/$/, "")) {
        const image0 = document.querySelector("#region_image_0")!;
        image0.parentNode!.insertBefore(this.cloud.foreignObject, image0);
        image0.parentNode!.insertBefore(
            this.main.foreignObject,
            image0.nextSibling,
        );
    }

    setActive(active: boolean) {
        this.active = active;
        this.lastComposeKey = "";
        this.main.foreignObject.style.display = active ? "block" : "none";
        this.cloud.foreignObject.style.display = active ? "block" : "none";
        if (!active) {
            this.clearCanvas(this.main.canvas);
            this.clearCanvas(this.cloud.canvas);
        }
    }

    isSupported(elements: CanvasTileElements) {
        return elements
            .filter((element) => element !== "")
            .every((element) => PMTILES_ELEMENTS.has(element));
    }

    async compose(
        initial: Dayjs,
        ft: Decimal,
        elements: CanvasTileElements,
        level: CanvasTileLevel,
        range: TileRange,
        clipCoast: boolean,
        model: MapModel,
    ) {
        if (!this.active) {
            return false;
        }
        // 同一条件の再compose (動画フレームの描画完了待ち→キャプチャの二重呼び等)
        // は前回結果をそのまま使う
        const composeKey = [
            initial.format("YYYYMMDD-HHmm"),
            elements.join(","),
            level,
            model,
            ft.toString(),
            `${range.left},${range.top},${range.right},${range.bottom}`,
            clipCoast ? "1" : "0",
        ].join("|");
        if (composeKey === this.lastComposeKey) {
            return true;
        }
        const sequence = ++this.composeSequence;
        const meta = getMapMeta(model);
        this.ensureCacheScope(initial, elements, model);
        this.ensureByteScope(initial, elements);
        this.applyClip(clipCoast);

        const mainElement = elements[0] || elements[2];
        const cloudElement = elements[1];
        await Promise.all([
            this.composeLayer(
                this.main.canvas,
                this.buildJobs(mainElement, initial, ft, level, range, meta),
                range,
                false,
                sequence,
                meta,
            ),
            this.composeLayer(
                this.cloud.canvas,
                this.buildJobs(cloudElement, initial, ft, level, range, meta),
                range,
                true,
                sequence,
                meta,
            ),
        ]);
        if (sequence !== this.composeSequence) {
            return false;
        }
        this.positionLayers(range, level, meta);
        this.lastComposeKey = composeKey;
        return true;
    }

    // 初回composeより前に、現在FTの表示タイル取得だけを開始する。
    // App.init()が下地地図を待っている間にPMTiles header/Rangeを並走させ、
    // compose時は同じbitmap Promiseを待つ。描画やactive状態はここでは変更しない。
    prefetchCurrent(
        initial: Dayjs,
        ft: Decimal,
        elements: CanvasTileElements,
        level: CanvasTileLevel,
        range: TileRange,
        model: MapModel,
    ) {
        const meta = getMapMeta(model);
        this.ensureCacheScope(initial, elements, model);
        this.ensureByteScope(initial, elements);
        const jobs = elements.flatMap((element) =>
            this.buildJobs(element, initial, ft, level, range, meta),
        );
        return Promise.all(jobs.map((job) => this.fetchBitmap(job))).then(
            () => undefined,
        );
    }

    applyClip(enabled: boolean) {
        const clipPath = enabled ? "url(#clip_coast)" : "";
        this.main.foreignObject.setAttribute("clip-path", clipPath);
        this.cloud.foreignObject.setAttribute("clip-path", clipPath);
    }

    // 表示近傍 (前方12・後方6) の先読み: bitmap 化まで行う。呼ばれるたびに世代を
    // 進め、未着手の旧世代ジョブ (跳ぶ前の窓の残り) は破棄する
    preload(
        initial: Dayjs,
        forecastTimes: Decimal[],
        elements: CanvasTileElements,
        level: CanvasTileLevel,
        range: TileRange,
        model: MapModel,
    ) {
        const meta = getMapMeta(model);
        this.prefetchQueue.newGeneration();
        for (const ft of forecastTimes) {
            this.enqueueFt(initial, ft, elements, level, range, meta);
        }
    }

    // 1時刻 (FT) 分を1ジョブとしてキューへ積む。ジョブ内のタイルは並列取得する。
    // タイル1枚ずつ直列に流すとエッジ未キャッシュ時のレイテンシ (~数百ms/リクエスト)
    // が積み上がるため、時刻単位で束ねて実効並列を上げる (実測 20秒→5秒)
    private enqueueFt(
        initial: Dayjs,
        ft: Decimal,
        elements: CanvasTileElements,
        level: CanvasTileLevel,
        range: TileRange,
        meta: MapMeta,
    ) {
        const jobs: TileJob[] = [];
        for (const element of elements) {
            for (const job of this.buildJobs(
                element,
                initial,
                ft,
                level,
                range,
                meta,
            )) {
                if (!this.bitmaps.has(job.key)) {
                    jobs.push(job);
                }
            }
        }
        if (jobs.length === 0) {
            return;
        }
        this.prefetchQueue.enqueue(() =>
            Promise.all(
                jobs.map((job) => this.fetchBitmap(job).catch(() => undefined)),
            ).then(() => this.progressCallback()),
        );
    }

    // 先読みジョブが1時刻ぶん完了するたびに呼ばれる (タイムバー配色の更新用)
    onPrefetchProgress(callback: () => void) {
        this.progressCallback = callback;
    }

    // 指定FTの表示に必要なタイルが全て取得済みか。bytes (圧縮キャッシュ) 命中で
    // 判定する — fetch 成功時は「タイル無し(null)」も含め必ず bytes へ入るため、
    // 取得の完了と等価。LRU で追い出された場合は未了へ戻る (正直な表示)
    ftReady(
        initial: Dayjs,
        ft: Decimal,
        elements: CanvasTileElements,
        level: CanvasTileLevel,
        range: TileRange,
        model: MapModel,
    ) {
        const meta = getMapMeta(model);
        for (const element of elements) {
            for (const job of this.buildJobs(
                element,
                initial,
                ft,
                level,
                range,
                meta,
            )) {
                if (!this.bytes.has(job.key)) {
                    return false;
                }
            }
        }
        return true;
    }

    private createLayer(id: string, smoothing: boolean) {
        const foreignObject = document.createElementNS(SVG_NS, "foreignObject");
        foreignObject.id = `${id}_foreign_object`;
        foreignObject.style.pointerEvents = "none";
        const canvas = document.createElement("canvas");
        canvas.id = id;
        canvas.dataset.smoothing = smoothing ? "1" : "0";
        canvas.style.display = "block";
        if (!smoothing) {
            canvas.className = "pixelated";
        }
        foreignObject.appendChild(canvas);
        return { foreignObject, canvas };
    }

    private buildJobs(
        element: string,
        initial: Dayjs,
        ft: Decimal,
        displayLevel: CanvasTileLevel,
        range: TileRange,
        meta: MapMeta,
    ) {
        if (!element) {
            return [];
        }
        const level = Math.min(
            displayLevel,
            element === "GSMC" ? 2 : 3,
        ) as CanvasTileLevel;
        const scale = 2 ** (displayLevel - level);
        const cropNum = meta.cropNum[level];
        const jobs: TileJob[] = [];
        const archiveUrl = this.archiveUrl(element, initial, ft);
        for (
            let row = Math.floor(range.top / scale);
            row < Math.ceil(range.bottom / scale);
            row++
        ) {
            if (row < 0 || row >= cropNum) {
                continue;
            }
            for (
                let column = Math.floor(range.left / scale);
                column < Math.ceil(range.right / scale);
                column++
            ) {
                if (column < 0 || column >= cropNum) {
                    continue;
                }
                jobs.push({
                    key: `${archiveUrl}|${level}|${row}-${column}`,
                    archiveUrl,
                    element,
                    level,
                    row,
                    column,
                    dx: (column * scale - range.left) * meta.tileW,
                    dy: (row * scale - range.top) * meta.tileH,
                    dw: meta.tileW * scale,
                    dh: meta.tileH * scale,
                });
            }
        }
        return jobs;
    }

    private async composeLayer(
        canvas: HTMLCanvasElement,
        jobs: TileJob[],
        range: TileRange,
        smoothing: boolean,
        sequence: number,
        meta: MapMeta,
    ) {
        const width = (range.right - range.left) * meta.tileW;
        const height = (range.bottom - range.top) * meta.tileH;
        // タイル取得失敗はここで rejection として親へ伝える (canvas は更新しない)。
        // RenderCoordinator がこの失敗を記録し waitForRender が失敗するため、
        // 動画生成に不完全なコマが混入しない安全装置になっている — 握りつぶさないこと
        const results = await Promise.all(
            jobs.map(async (job) => ({
                job,
                bitmap: await this.fetchBitmap(job),
            })),
        );
        if (sequence !== this.composeSequence) {
            return;
        }
        canvas.width = Math.max(1, width);
        canvas.height = Math.max(1, height);
        canvas.dataset.sourceLevel = jobs[0] ? String(jobs[0].level) : "";
        const context = canvas.getContext("2d", { alpha: true })!;
        context.imageSmoothingEnabled = smoothing;
        let drewAny = false;
        for (const { job, bitmap } of results) {
            if (bitmap) {
                context.drawImage(bitmap, job.dx, job.dy, job.dw, job.dh);
                drewAny = true;
            }
        }
        // 1画素も描いていないcanvas (雲なし要素の雲層など) はRasterizerが
        // PNG化を省略できるよう記録する (動画フレーム生成の高速化)
        canvas.dataset.blank = drewAny ? "0" : "1";
    }

    private fetchBitmap(job: TileJob) {
        const cached = this.bitmaps.get(job.key);
        if (cached) {
            return cached;
        }
        const promise = this.fetchSource(job)
            .then(async (source) => {
                if (!source.data) {
                    return null;
                }
                const style = snowStyleOf(job.element);
                const data = style
                    ? applySnowPalette(source.data, style)
                    : source.data;
                const bitmap = await createImageBitmap(new Blob([data]));
                if (source.factor === 1) {
                    return bitmap;
                }
                const width = bitmap.width / source.factor;
                const height = bitmap.height / source.factor;
                const cropped = await createImageBitmap(
                    bitmap,
                    source.offsetX * width,
                    source.offsetY * height,
                    width,
                    height,
                );
                bitmap.close();
                return cropped;
            })
            .catch((error) => {
                this.bitmaps.delete(job.key);
                throw error;
            });
        this.bitmaps.set(job.key, promise);
        this.bitmapOrder.push(job.key);
        // デコード済み RGBA は1枚~1.7MB あるため上限は増やさない (最悪~1.7GB)。
        // 追い出されても bytes キャッシュ (圧縮) から再デコードのみで戻せる
        while (this.bitmapOrder.length > 1024) {
            const oldest = this.bitmapOrder.shift()!;
            const bitmap = this.bitmaps.get(oldest);
            this.bitmaps.delete(oldest);
            bitmap?.then((value) => value?.close()).catch(() => undefined);
        }
        return promise;
    }

    private getArchive(url: string) {
        const cached = this.archives.get(url);
        if (cached) {
            return cached;
        }
        const promise = Promise.resolve(new PMTiles(url)).then(
            async (archive) => {
                await archive.getHeader();
                return archive;
            },
        );
        // 取得失敗をキャッシュに残さない (入電直後の一時的な404がリロードまで粘着するのを防ぐ)
        promise.catch(() => this.archives.delete(url));
        this.archives.set(url, promise);
        this.archiveOrder.push(url);
        // タイムバー全体 (最大89時刻×2レイヤ=178本) が収まるだけ保持する (2026-08-02)
        while (this.archiveOrder.length > 256) {
            this.archives.delete(this.archiveOrder.shift()!);
        }
        return promise;
    }

    private async fetchSource(job: TileJob): Promise<TileSource> {
        const archive = await this.getArchive(job.archiveUrl);
        const header = await archive.getHeader();
        const requestedZoom = job.level + 1;
        // 過去PMTilesは上位Lvだけdropされる。親tileの該当部分を切り出して
        // 拡大することで、要求画角を空白にせず表示する。
        const sourceZoom = Math.min(requestedZoom, header.maxZoom);
        const shift = requestedZoom - sourceZoom;
        const factor = 2 ** shift;
        const sourceColumn = Math.floor(job.column / factor);
        const sourceRow = Math.floor(job.row / factor);
        let data = this.bytes.get(job.key);
        if (data === undefined) {
            const response = await archive.getZxy(
                sourceZoom,
                sourceColumn,
                sourceRow,
            );
            data = response?.data || null;
        }
        // 取得したタイルは圧縮のまま全て保持する (LRU 200MB)。タイムバー全体
        // キャッシュの実体で、bitmap LRU から追い出されても再取得なしで戻せる。
        // 旧実装は .0 (と LFM .5) だけ保持していたが全FTへ拡大 (2026-08-02)
        this.storeBytes(job.key, data);
        return {
            data,
            factor,
            offsetX: job.column % factor,
            offsetY: job.row % factor,
        };
    }

    private storeBytes(key: string, data: ArrayBuffer | null) {
        if (this.bytes.has(key)) {
            return;
        }
        this.bytes.set(key, data);
        this.byteOrder.push(key);
        this.byteSize += data?.byteLength || 0;
        const maxBytes = 200 * 1024 * 1024;
        while (this.byteSize > maxBytes && this.byteOrder.length > 0) {
            const oldest = this.byteOrder.shift()!;
            const oldData = this.bytes.get(oldest);
            this.bytes.delete(oldest);
            this.byteSize -= oldData?.byteLength || 0;
        }
    }

    private ensureByteScope(initial: Dayjs, elements: CanvasTileElements) {
        const scope = `${initial.format("YYYYMMDD-HH")}|${elements.join(",")}`;
        if (scope === this.byteScope) {
            return;
        }
        this.bytes.clear();
        this.byteOrder.length = 0;
        this.byteSize = 0;
        this.byteScope = scope;
    }

    private archiveUrl(element: string, initial: Dayjs, ft: Decimal) {
        if (element === "GSLR" && usesGslAnalysis(ft)) {
            const { target, key } = gslTargetKey(initial, ft);
            return `${this.dataBase}/GSLR/${target.format("YYYYMM")}/analysis/GSLR-A_${key}.pmtiles`;
        }
        if (element === "MSGL" && usesMsgAnalysis(ft)) {
            const { target, key } = msgTargetKey(initial, ft);
            return `${this.dataBase}/MSGL/${target.format("YYYYMM")}/analysis/MSGL-A_${key}.pmtiles`;
        }
        if (element.startsWith("QMA")) {
            const wholeHours = ft.floor().toNumber();
            const decimal = ft.sub(wholeHours).times(10).toNumber();
            const target = initial.add(ft.toNumber(), "hour");
            const directory = initial.add(ft.ceil().toNumber(), "hour");
            return `${this.dataBase}/${element}/${directory.format("YYYYMM")}/${directory.format("YYYYMMDD-HH")}/${element}_${target.format("YYYYMMDD-HH")}.${decimal}.pmtiles`;
        }
        // タスクS統合: 雪3スタイルは統合要素 <model>S を読む (R2/dat の dir は従来どおり <model>Sh)
        const snowStorage = snowStorageOf(element);
        if (snowStorage) {
            const ini = initial.format("YYYYMMDD-HH");
            const w = /^(GS5|GSA|GSL)/.test(snowStorage) ? 5 : 4;
            const fts = ft.toFixed(1).padStart(w, "0");
            const dir = `${snowStorage}h`;
            return `${this.dataBase}/${dir}/${initial.format("YYYYMM")}/${ini}/${snowStorage}_${ini}_FT${fts}.pmtiles`;
        }
        const initialString = initial.format("YYYYMMDD-HH");
        const width = /^(GS5|GSA|GSL)/.test(element) ? 5 : 4;
        const ftString = element.startsWith("GSL")
            ? gslForecastKey(ft)
            : element === "MSGL"
              ? msgForecastKey(ft)
              : ft.toFixed(1).padStart(width, "0");
        return `${this.dataBase}/${element}/${initial.format("YYYYMM")}/${initialString}/${element}_${initialString}_FT${ftString}.pmtiles`;
    }

    private ensureCacheScope(
        initial: Dayjs,
        elements: CanvasTileElements,
        model: MapModel,
    ) {
        // レベルは scope に含めない (2026-08-02): キャッシュキーがレベルを含むため
        // 混在しても正しく引ける。ズーム変更で ±6 窓の先読み資産を捨てない
        const scope = `${model}|${initial.format("YYYYMMDD-HH")}|${elements.join(",")}`;
        if (scope === this.cacheScope) {
            return;
        }
        for (const bitmap of this.bitmaps.values()) {
            bitmap.then((value) => value?.close()).catch(() => undefined);
        }
        this.bitmaps.clear();
        this.bitmapOrder.length = 0;
        this.cacheScope = scope;
    }

    private positionLayers(
        range: TileRange,
        level: CanvasTileLevel,
        meta: MapMeta,
    ) {
        const cropNum = meta.cropNum[level];
        const tileWidth = meta.width / cropNum;
        const tileHeight = meta.height / cropNum;
        const x = meta.x0 + range.left * tileWidth;
        const y = meta.y0 + range.top * tileHeight;
        const width = (range.right - range.left) * tileWidth;
        const height = (range.bottom - range.top) * tileHeight;
        for (const { foreignObject, canvas } of [this.main, this.cloud]) {
            foreignObject.setAttribute("x", String(x));
            foreignObject.setAttribute("y", String(y));
            foreignObject.setAttribute("width", String(Math.max(width, 0.001)));
            foreignObject.setAttribute(
                "height",
                String(Math.max(height, 0.001)),
            );
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            canvas.dataset.displayLevel = String(level);
            // Chromium は動的挿入した foreignObject のサイズ属性変更で再レイアウトされない
            // ことがある (GPV-1.2 実装時に実測)。挿し直しで強制する。
            const parent = foreignObject.parentNode!;
            const next = foreignObject.nextSibling;
            parent.removeChild(foreignObject);
            parent.insertBefore(foreignObject, next);
        }
    }

    private clearCanvas(canvas: HTMLCanvasElement) {
        canvas.width = 1;
        canvas.height = 1;
    }
}
